import type { PropertySchemaAssociationInput } from "~/common/types";
import type {
  PropertySchemaRow,
  RecordMetadataSchemaDetailRaw,
  RecordMetadataSchemaDetailUI,
  RecordMetadataSchemaFormData,
  RecordMetadataSchemaListRaw,
  RecordMetadataSchemaRow,
} from "~/features/admin/recordMetadataSchema/types";

type AssociationFormData = NonNullable<RecordMetadataSchemaFormData["propertySchemaAssociations"]>[number];

/** API row → table row. */
function toRecordMetadataSchemaRow(raw: RecordMetadataSchemaListRaw): RecordMetadataSchemaRow {
  return {
    id: raw.id,
    isDeactivated: Boolean(raw.deactivatedAt),
  };
}

export function toRecordMetadataSchemaRowList(raws: readonly RecordMetadataSchemaListRaw[]): RecordMetadataSchemaRow[] {
  return raws.map(toRecordMetadataSchemaRow);
}

/** API detail row → detail-page view model. */
export function toRecordMetadataSchemaDetailUI(raw: RecordMetadataSchemaDetailRaw): RecordMetadataSchemaDetailUI {
  const propertySchemas: PropertySchemaRow[] = raw.propertySchemaAssociations.map((a) => ({
    id: a.id,
    name: a.name,
    description: a.description ?? "",
    position: a.position,
    required: a.required,
    unique: a.unique,
    propertyTypeId: a.propertySchema.propertyTypeId,
    propertyAttributeTypeId: a.propertyAttributeTypeId ?? null,
    selectOptions: Array.isArray(a.propertySchema.selectOptions)
      ? (a.propertySchema.selectOptions as PropertySchemaRow["selectOptions"])
      : [],
    urlAllowList: Array.isArray(a.propertySchema.urlAllowList)
      ? (a.propertySchema.urlAllowList as PropertySchemaRow["urlAllowList"])
      : [],
  }));

  return {
    id: raw.id,
    isDeactivated: Boolean(raw.deactivatedAt),
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
    deactivatedAt: raw.deactivatedAt ?? null,
    propertySchemas,
  };
}

/** Form rows → mutation input. Explicit field mapping avoids a structural double-cast. */
export function toPropertySchemaAssociationInputList(
  associations: AssociationFormData[],
): PropertySchemaAssociationInput[] {
  return associations.map((a) => ({
    id: a.id,
    name: a.name,
    description: a.description,
    position: a.position,
    required: a.required,
    unique: a.unique,
    propertyAttributeTypeId: a.propertyAttributeTypeId,
    propertySchema: {
      id: a.propertySchema.id,
      propertyTypeId: a.propertySchema.propertyTypeId,
      selectOptions: a.propertySchema.selectOptions,
      urlAllowList: a.propertySchema.urlAllowList,
    },
  }));
}
